import { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";
import { adminApi } from "../lib/adminApi";
import {
  DashboardIcon, ActivityIcon, UsersIcon, BillingIcon, TransactionsIcon,
  PowerIcon, MenuIcon, ChevronLeftIcon, ClockIcon,
} from "../components/Icons";

function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useState(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  });
  return w;
}

// ── Internal nav items ───────────────────────────────────────
const NAV = [
  { path: "/admin",           Icon: DashboardIcon,    label: "Overview"  },
  { path: "/admin/activity",  Icon: ActivityIcon,     label: "Activity"  },
  { path: "/admin/monitor",   Icon: ActivityIcon,     label: "Monitor"   },
  { path: "/admin/owners",    Icon: UsersIcon,        label: "Owners"    },
  { path: "/admin/plans",     Icon: BillingIcon,      label: "Plans"     },
  { path: "/admin/payments",  Icon: TransactionsIcon, label: "Payments"  },
  { path: "/admin/audit",     Icon: ClockIcon,        label: "Audit Log" },
];

// ── Replace this with your Sentry project URL ────────────────
// e.g. https://acme.sentry.io/issues/?project=123456
const SENTRY_URL = import.meta.env.VITE_SENTRY_URL ?? "https://sentry.io";

const PURPLE = "#8b5cf6";
const BG     = "#080c12";
const CARD   = "#0d1117";
const BORDER = "rgba(255,255,255,0.07)";
const MONO   = "DM Mono, monospace";
const SANS   = "DM Sans, sans-serif";

/** Small "opens in new tab" arrow icon */
function ExternalIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" style={{ flexShrink: 0, opacity: 0.5 }}>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

/** Shield icon for admin brand */
function ShieldIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

export default function AdminLayout() {
  const { profile, signOut } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const width     = useWindowWidth();
  const isMobile  = width < 768;
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  // Log every admin page access (fire-and-forget — never blocks navigation)
  useEffect(() => {
    adminApi.logAdminAccess(location.pathname, profile?.name ?? "Admin").catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    supabase.from("admin_events").select("id", { count: "exact", head: true }).is("read_at", null)
      .then(({ count }) => setUnread(count ?? 0));
    const ch = supabase.channel("admin-layout-badge")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "admin_events" },
        () => setUnread(n => n + 1))
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "admin_events" },
        () => {
          supabase.from("admin_events").select("id", { count: "exact", head: true }).is("read_at", null)
            .then(({ count }) => setUnread(count ?? 0));
        })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const sidebarW = 230;

  const SidebarContent = () => (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>

      {/* Logo */}
      <div style={{ padding: "22px 20px 18px", borderBottom: `1px solid ${BORDER}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: `linear-gradient(135deg, ${PURPLE}, #7c3aed)`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff",
          }}>
            <ShieldIcon />
          </div>
          <div>
            <div style={{ fontFamily: SANS, fontWeight: 800, fontSize: 15, color: "#f9fafb" }}>Admin Panel</div>
            <div style={{ fontFamily: MONO, fontSize: 10, color: PURPLE, fontWeight: 600, letterSpacing: "0.06em" }}>SUPERUSER</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav aria-label="Admin navigation" style={{ flex: 1, padding: "14px 10px", display: "flex", flexDirection: "column", gap: 2 }}>
        {NAV.map(({ path, Icon, label }) => {
          const active = path === "/admin"
            ? location.pathname === "/admin"
            : location.pathname.startsWith(path);
          return (
            <button
              key={path}
              onClick={() => { navigate(path); if (isMobile) setOpen(false); }}
              aria-current={active ? "page" : undefined}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "10px 14px", borderRadius: 10, border: "none", cursor: "pointer",
                background: active ? `rgba(139,92,246,0.12)` : "transparent",
                color: active ? PURPLE : "rgba(255,255,255,0.55)",
                fontFamily: SANS, fontWeight: active ? 700 : 500, fontSize: 14,
                textAlign: "left", width: "100%",
                borderLeft: active ? `3px solid ${PURPLE}` : "3px solid transparent",
                transition: "all 0.15s",
              }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.background = "transparent"; }}
            >
              <Icon size={16} color={active ? PURPLE : "rgba(255,255,255,0.45)"} />
              <span style={{ flex: 1 }}>{label}</span>
              {path === "/admin/activity" && unread > 0 && (
                <span style={{ background: "#f87171", color: "#fff", fontFamily: MONO, fontSize: 9, fontWeight: 700, borderRadius: 99, padding: "2px 6px", minWidth: 16, textAlign: "center" }}>
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </button>
          );
        })}

        {/* Divider */}
        <div style={{ height: 1, background: BORDER, margin: "8px 4px" }} />

        {/* Sentry — external link */}
        <a
          href={SENTRY_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open Sentry error monitoring (opens in new tab)"
          style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "10px 14px", borderRadius: 10,
            color: "rgba(255,255,255,0.45)",
            fontFamily: SANS, fontWeight: 500, fontSize: 14,
            textDecoration: "none",
            borderLeft: "3px solid transparent",
            transition: "all 0.15s",
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = "rgba(240,97,52,0.08)";
            e.currentTarget.style.color = "#f06134";
            e.currentTarget.style.borderLeftColor = "rgba(240,97,52,0.4)";
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "rgba(255,255,255,0.45)";
            e.currentTarget.style.borderLeftColor = "transparent";
          }}
        >
          {/* Sentry logo mark — the circle+fork shape */}
          <svg width="16" height="16" viewBox="0 0 72 66" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
            <path d="M29.97 2.68a9.5 9.5 0 0 1 12.06 0l27.5 23.8A9.5 9.5 0 0 1 72 33.7v.2l-7.3 6.3a2 2 0 0 1-2.76-.22l-3.1-3.58a2 2 0 0 1 .22-2.82L63 30.44a1.5 1.5 0 0 0 .18-2.1L37.5 3.76a1.5 1.5 0 0 0-2.0 0l-7.3 6.32L14.4 1.96 29.97 2.68zM2.47 40.52l7.3-6.3a2 2 0 0 1 2.76.22l3.1 3.58a2 2 0 0 1-.22 2.82L11.46 44.4a1.5 1.5 0 0 0-.18 2.1l18.3 21.14a1.5 1.5 0 0 0 2.0 0l4.42-3.82 13.7 8.12L35.03 63.3a9.5 9.5 0 0 1-12.06 0L.5 39.5a9.5 9.5 0 0 1 0-14.5l1.97 15.52z" />
          </svg>
          <span style={{ flex: 1 }}>Sentry Errors</span>
          <ExternalIcon />
        </a>
      </nav>

      {/* User footer */}
      <div style={{ padding: "14px 16px", borderTop: `1px solid ${BORDER}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: "50%",
            background: `linear-gradient(135deg,${PURPLE},#7c3aed)`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: SANS, fontWeight: 700, fontSize: 13, color: "#fff", flexShrink: 0,
          }}>
            {profile?.name?.[0]?.toUpperCase() ?? "A"}
          </div>
          <div style={{ overflow: "hidden" }}>
            <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: "#f9fafb", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {profile?.name ?? "Admin"}
            </div>
            <div style={{ fontFamily: MONO, fontSize: 10, color: PURPLE }}>Administrator</div>
          </div>
        </div>
        <button
          onClick={signOut}
          aria-label="Sign out"
          style={{
            width: "100%", padding: "8px 0",
            background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.2)",
            borderRadius: 8, color: "#f87171", fontFamily: MONO, fontSize: 11,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
            transition: "background 0.15s",
          }}
          onMouseEnter={e => (e.currentTarget.style.background = "rgba(248,113,113,0.15)")}
          onMouseLeave={e => (e.currentTarget.style.background = "rgba(248,113,113,0.08)")}
        >
          <PowerIcon size={12} color="#f87171" /> Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", minHeight: "100svh", background: BG, color: "#f9fafb" }}>

      {/* Desktop sidebar */}
      {!isMobile && (
        <aside style={{ width: sidebarW, flexShrink: 0, background: CARD, borderRight: `1px solid ${BORDER}`, position: "fixed", top: 0, left: 0, height: "100vh", zIndex: 40, overflowY: "auto" }}>
          <SidebarContent />
        </aside>
      )}

      {/* Mobile sidebar overlay */}
      {isMobile && open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 40 }} />
          <aside style={{ position: "fixed", top: 0, left: 0, width: sidebarW, height: "100vh", background: CARD, borderRight: `1px solid ${BORDER}`, zIndex: 50, overflowY: "auto" }}>
            <SidebarContent />
          </aside>
        </>
      )}

      {/* Main content */}
      <div style={{ flex: 1, marginLeft: isMobile ? 0 : sidebarW, display: "flex", flexDirection: "column", minHeight: "100svh" }}>

        {/* Top bar */}
        <header style={{ height: 58, background: CARD, borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px", position: "sticky", top: 0, zIndex: 30 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {isMobile && (
              <button
                onClick={() => setOpen(true)}
                aria-label="Open navigation menu"
                style={{ background: "none", border: "none", color: "#f9fafb", cursor: "pointer", padding: 4, display: "flex" }}>
                <MenuIcon size={20} />
              </button>
            )}
            <div style={{ fontFamily: MONO, fontSize: 11, color: PURPLE, background: "rgba(139,92,246,0.1)", border: `1px solid rgba(139,92,246,0.25)`, borderRadius: 6, padding: "3px 10px", fontWeight: 600, letterSpacing: "0.08em" }}>
              ADMIN
            </div>
            <span style={{ fontFamily: SANS, fontWeight: 700, fontSize: 15, color: "#f9fafb" }}>
              {NAV.find(n => n.path === "/admin" ? location.pathname === "/admin" : location.pathname.startsWith(n.path))?.label ?? "Audit Log"}
            </span>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${BORDER}`, borderRadius: 8, padding: "6px 14px", color: "rgba(255,255,255,0.5)", fontFamily: MONO, fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, transition: "background 0.15s" }}
            onMouseEnter={e => (e.currentTarget.style.background = "rgba(255,255,255,0.08)")}
            onMouseLeave={e => (e.currentTarget.style.background = "rgba(255,255,255,0.04)")}
          >
            <ChevronLeftIcon size={13} /> Owner View
          </button>
        </header>

        {/* Page content */}
        <main style={{ flex: 1, padding: isMobile ? "16px 14px" : "28px 32px", maxWidth: 1200, width: "100%", boxSizing: "border-box" }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
